import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import {
  recordServiceCall as __snRecordServiceCall,
  __resetForTest as __snResetServiceHealthForTest,
} from './hooks/serviceHealthRegistry'
import { ECS_FLAGS } from './config/featureFlags'
import { initWebVitals } from './utils/webVitals'

// WCAG dev-time accessibility audit (zero production bundle impact).
// axe-core logs violations to the browser console during development.
if (import.meta.env.DEV) {
  import('@axe-core/react').then(({ default: axe }) => {
    axe(React, ReactDOM, 1000);
  });
}

// Stylesheets — imported before App so cascade order is correct
import './styles/design-system-2.0/index.scss';
import './styles/constitutional.css';
import './index.css'
import './layout-fixes.css'

// Lazy-load AppShell (wraps QueryClientProvider + App) to keep the entry
// chunk minimal. This prevents Rollup from inlining shared app code into
// the entry, which causes lazy↔entry circular chunk deps → TDZ errors.
const AppShell = lazy(() => import('./AppShell'))

// Catch async Promise rejections not caught by the render-phase ErrorBoundary
window.addEventListener('unhandledrejection', (event) => {
  console.error('[UnhandledRejection]', event.reason);
});

// Cypress hook: expose the D10 service-health registry's mutators on the
// window synchronously at entry-chunk load — before any lazy chunk
// (AppShell, App, routes) resolves. React Query's per-retry errors don't
// bubble up to QueryCache.subscribe (only the final error fires once), so
// the registry's 3-failure threshold isn't reachable from in-spec
// cy.intercept 503s alone — Cypress specs seed UNAVAILABLE state via
// window.__sn_serviceHealthRegistry.recordServiceCall, mirroring the
// Vitest pattern at WeeklyPlanDetailPageDegradedState.test.tsx:72-76.
(window as unknown as {
  __sn_serviceHealthRegistry?: {
    recordServiceCall: typeof __snRecordServiceCall;
    __resetForTest: typeof __snResetServiceHealthForTest;
  };
}).__sn_serviceHealthRegistry = {
  recordServiceCall: __snRecordServiceCall,
  __resetForTest: __snResetServiceHealthForTest,
};

// Core Web Vitals field RUM beacon (SN-IMPL-PRODREADY-001 S3.2/B15, gate G3.2;
// ADR-0098). Flag-gated at build time (VITE_ENABLE_RUM); the beacon carries no
// PII and silently degrades if web-vitals is unavailable or offline.
if (ECS_FLAGS.enableRum) {
  void initWebVitals();
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <Suspense fallback={
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: '#0F0F12',
      color: '#D4A843',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '1.125rem',
    }}>
      Loading Sovereign Nexus…
    </div>
  }>
    <AppShell />
  </Suspense>
)
